import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  User,
  BookOpen,
  LogOut,
  FileText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CoachProfile from "./coach/CoachProfile";
import CoachTraining from "./coach/CoachTraining";
import CoachDocuments from "./coach/CoachDocuments";

const CoachDashboard = () => {
  const { coach, coachLogout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");

  const handleLogout = async () => {
    await coachLogout();
    navigate("/coach/login");
  };

  if (!coach) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const docsValidated = coach.documents_validated;
  const trainingUnlocked = coach.has_training_access && docsValidated;

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="coach-dashboard">
      {/* Header */}
      <header className="bg-[#0F0F16] border-b border-white/10 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_coach-hub-96/artifacts/uwrqhh9u_1777073959823.png"
              alt="APM Logo"
              className="h-10 w-auto invert"
            />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-[#94A3B8] text-sm hidden sm:block">
              Bienvenue, <span className="text-white font-semibold">{coach.name}</span>
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-500 hover:text-red-400 transition-colors text-sm"
              data-testid="coach-logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/10 pb-4 flex-wrap">
          <button
            onClick={() => setActiveTab("profile")}
            className={`flex items-center gap-2 px-4 py-2 font-semibold transition-colors ${
              activeTab === "profile"
                ? "text-[#0066FF] border-b-2 border-[#0066FF]"
                : "text-[#94A3B8] hover:text-white"
            }`}
            data-testid="tab-profile"
          >
            <User className="w-5 h-5" />
            Mon Profil
          </button>
          <button
            onClick={() => setActiveTab("documents")}
            className={`flex items-center gap-2 px-4 py-2 font-semibold transition-colors ${
              activeTab === "documents"
                ? "text-[#0066FF] border-b-2 border-[#0066FF]"
                : "text-[#94A3B8] hover:text-white"
            }`}
            data-testid="tab-documents"
          >
            <FileText className="w-5 h-5" />
            Documents
            {!docsValidated && (
              <span className="ml-1 w-2 h-2 bg-yellow-500 rounded-full" data-testid="docs-pending-dot"></span>
            )}
          </button>
          {coach.has_training_access && (
            <button
              onClick={() => setActiveTab("training")}
              disabled={!trainingUnlocked}
              className={`flex items-center gap-2 px-4 py-2 font-semibold transition-colors ${
                activeTab === "training"
                  ? "text-[#0066FF] border-b-2 border-[#0066FF]"
                  : trainingUnlocked
                    ? "text-[#94A3B8] hover:text-white"
                    : "text-[#64748B] cursor-not-allowed opacity-60"
              }`}
              data-testid="tab-training"
              title={trainingUnlocked ? "" : "Disponible après validation de vos documents"}
            >
              <BookOpen className="w-5 h-5" />
              Formation
              {!trainingUnlocked && <span className="text-xs">🔒</span>}
            </button>
          )}
        </div>

        {activeTab === "profile" && (
          <CoachProfile coach={coach} onProfileUpdated={checkAuth} />
        )}

        {activeTab === "documents" && (
          <CoachDocuments coach={coach} onUpdated={checkAuth} />
        )}

        {activeTab === "training" && trainingUnlocked && (
          <CoachTraining hasAccess={coach.has_training_access} />
        )}
      </div>
    </div>
  );
};

export default CoachDashboard;
